const STATE_ABBREVIATIONS = {
  alabama: "al",
  alaska: "ak",
  arizona: "az",
  arkansas: "ar",
  california: "ca",
  colorado: "co",
  connecticut: "ct",
  delaware: "de",
  florida: "fl",
  georgia: "ga",
  hawaii: "hi",
  idaho: "id",
  illinois: "il",
  indiana: "in",
  iowa: "ia",
  kansas: "ks",
  kentucky: "ky",
  louisiana: "la",
  maine: "me",
  maryland: "md",
  massachusetts: "ma",
  michigan: "mi",
  minnesota: "mn",
  mississippi: "ms",
  missouri: "mo",
  montana: "mt",
  nebraska: "ne",
  nevada: "nv",
  "new hampshire": "nh",
  "new jersey": "nj",
  "new mexico": "nm",
  "new york": "ny",
  "north carolina": "nc",
  "north dakota": "nd",
  ohio: "oh",
  oklahoma: "ok",
  oregon: "or",
  pennsylvania: "pa",
  "rhode island": "ri",
  "south carolina": "sc",
  "south dakota": "sd",
  tennessee: "tn",
  texas: "tx",
  utah: "ut",
  vermont: "vt",
  virginia: "va",
  washington: "wa",
  "west virginia": "wv",
  wisconsin: "wi",
  wyoming: "wy",
  "district of columbia": "dc",
};

class ProvidersMap extends HTMLElement {
  constructor() {
    super();
    this.providers = [];
    this.providersLoaded = false;
    this.filteredProviders = [];
    this.searchQuery = "";
    this.searchLocation = null;
    this.searchFallback = null;
    this.activeDistanceOrigin = null;
    this.userLocation = null;
    this.map = null;
    this.geocoder = null;
    this.markers = [];
    this.userLocationMarker = null;
    this.searchLocationMarker = null;
    this.activeInfoWindow = null;
    this.mobileMediaQuery = null;
    this.initialMapSelect = false;
    this.activeRadiusMiles = null;
    this.searchRequestToken = 0;
    this.searchDebounceDelay = 240;
    this.pendingSearchTimeout = null;
    this.mapUpdateDebounceDelay = 320;
    this.pendingMapUpdateTimeout = null;
    this.renderRequestToken = 0;
    this.pendingRenderFrame = null;
    this.mobileRenderChunkThreshold = 80;
    this.mobileRenderChunkSize = 40;
    this.minimumNearbyResults = 8;
    this.maximumExpandedResults = 8;
    this.nearbyRadiusSteps = [10, 25, 50, 100, 250, 500, 1000, Infinity];
  }

  connectedCallback() {
    if (this.hasAttribute('data-defer-init')) return;
    this.init();
  }

  init() {
    this.loadProviders();
    this.setupMobileViewToggle();
    this.setupResponsiveSearchPlaceholder();
    this.setupSearch();
    this.setupCurrentLocationButton();
    this.loadGoogleMaps();
  }

  setupMobileViewToggle() {
    const toggle = this.querySelector("[data-mobile-view-toggle]");
    const triggerButtons = this.querySelectorAll("[data-mobile-view-trigger]");

    this.mobileMediaQuery = window.matchMedia("(max-width: 768px)");

    const syncMobileView = () => {
      if (this.mobileMediaQuery.matches) {
        if (!this.dataset.mobileView) {
          this.setMobileView("list");
        } else {
          this.updateMobileViewButtons();
        }
      } else {
        this.dataset.mobileView = "split";
        this.updateMobileViewButtons();
      }

      if (toggle) {
        toggle.hidden = !this.mobileMediaQuery.matches;
      }
    };

    triggerButtons.forEach((button) => {
      button.addEventListener("click", () => {
        this.setMobileView(button.dataset.mobileViewTrigger || "list");
      });
    });

    if (typeof this.mobileMediaQuery.addEventListener === "function") {
      this.mobileMediaQuery.addEventListener("change", syncMobileView);
    } else {
      this.mobileMediaQuery.addListener(syncMobileView);
    }

    syncMobileView();
  }

  setMobileView(view) {
    const allowedView = view === "map" ? "map" : "list";
    this.dataset.mobileView = allowedView;
    this.updateMobileViewButtons();

    if (
      allowedView === "map" &&
      this.mobileMediaQuery?.matches &&
      this.map &&
      window.google &&
      window.google.maps
    ) {
      this.clearPendingMapUpdate();
      this.updateMapMarkers();
      requestAnimationFrame(() => {
        google.maps.event.trigger(this.map, "resize");
        this.updateMapViewport();
      });
    }
  }

  updateMobileViewButtons() {
    const currentView = this.dataset.mobileView;

    this.querySelectorAll("[data-mobile-view-trigger]").forEach((button) => {
      const isActive = button.dataset.mobileViewTrigger === currentView;
      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-pressed", isActive ? "true" : "false");
    });
  }

  setupResponsiveSearchPlaceholder() {
    const searchInput = this.querySelector("#provider-search");
    if (!searchInput) return;

    const syncPlaceholder = () => {
      const desktopPlaceholder =
        searchInput.dataset.desktopPlaceholder || searchInput.placeholder;
      const mobilePlaceholder =
        searchInput.dataset.mobilePlaceholder || desktopPlaceholder;

      searchInput.placeholder = this.mobileMediaQuery?.matches
        ? mobilePlaceholder
        : desktopPlaceholder;
    };

    if (this.mobileMediaQuery) {
      if (typeof this.mobileMediaQuery.addEventListener === "function") {
        this.mobileMediaQuery.addEventListener("change", syncPlaceholder);
      } else {
        this.mobileMediaQuery.addListener(syncPlaceholder);
      }
    }

    syncPlaceholder();
  }

  async loadProviders() {
    try {
      this.showLoadingState();

      const response = await fetch("https://app.xtresse.com/api/locations");

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || "Failed to fetch providers");
      }

      const apiProviders = Array.isArray(data.data) ? data.data : [];
      this.providers = apiProviders.map((provider) => this.decorateProvider(provider));
      this.providersLoaded = true;
      this.applyFilters();
      this.hideLoadingState();
    } catch (error) {
      console.error("Error fetching provider data:", error);
      this.providers = [];
      this.filteredProviders = [];
      this.providersLoaded = true;
      this.applyFilters();
      this.hideLoadingState();
    }
  }

  showLoadingState() {
    const resultsList = this.querySelector("#provider-list");
    const resultsCount = this.querySelector(".results-count");
    const resultsSummary = this.querySelector("[data-results-summary]");

    if (resultsList) {
      resultsList.innerHTML = `
        <div style="text-align: center; padding: 40px 20px; color: var(--text-light);">
          <div class="loading-spinner"></div>
          Loading providers...
        </div>
      `;
    }

    if (resultsCount) {
      resultsCount.textContent = "Loading...";
    }

    if (resultsSummary) {
      resultsSummary.textContent = "";
    }
  }

  hideLoadingState() {}

  setupSearch() {
    const searchInput = this.querySelector("#provider-search");
    const searchButton = this.querySelector(".search-button");

    if (!searchInput) return;

    const runSearch = ({ deferMapUpdate = false } = {}) => {
      this.clearPendingSearch();
      this.searchQuery = searchInput.value.trim();
      this.applyFilters({ deferMapUpdate });
    };

    const scheduleSearch = () => {
      this.clearPendingSearch();

      this.pendingSearchTimeout = window.setTimeout(() => {
        this.pendingSearchTimeout = null;
        runSearch({ deferMapUpdate: true });
      }, this.searchDebounceDelay);
    };

    searchInput.addEventListener("input", scheduleSearch);

    if (searchButton) {
      searchButton.addEventListener("click", () => runSearch());
    }

    searchInput.addEventListener("keypress", (event) => {
      if (event.key === "Enter") {
        runSearch();
      }
    });
  }

  clearPendingSearch() {
    if (this.pendingSearchTimeout) {
      window.clearTimeout(this.pendingSearchTimeout);
      this.pendingSearchTimeout = null;
    }
  }

  clearPendingMapUpdate() {
    if (this.pendingMapUpdateTimeout) {
      window.clearTimeout(this.pendingMapUpdateTimeout);
      this.pendingMapUpdateTimeout = null;
    }
  }

  clearPendingRender() {
    if (this.pendingRenderFrame) {
      window.cancelAnimationFrame(this.pendingRenderFrame);
      this.pendingRenderFrame = null;
    }
  }

  isMobileListViewActive() {
    return this.mobileMediaQuery?.matches && this.dataset.mobileView !== "map";
  }

  scheduleMapUpdate() {
    if (!this.map || this.isMobileListViewActive()) return;

    this.clearPendingMapUpdate();
    this.pendingMapUpdateTimeout = window.setTimeout(() => {
      this.pendingMapUpdateTimeout = null;
      this.updateMapMarkers();
    }, this.mapUpdateDebounceDelay);
  }

  setupCurrentLocationButton() {
    const currentLocationButton = this.querySelector(".current-location-button");

    if (!currentLocationButton) return;

    currentLocationButton.dataset.defaultLabel =
      currentLocationButton.dataset.defaultLabel ||
      currentLocationButton.textContent.trim();

    currentLocationButton.addEventListener("click", () => {
      this.getCurrentLocationAndCenterMap(true);
    });
  }

  getCurrentLocationAndCenterMap(showFeedback = false) {
    const currentLocationButton = this.querySelector(".current-location-button");

    if (!navigator.geolocation) {
      if (showFeedback) {
        alert("Geolocation is not supported by this browser.");
      }
      return;
    }

    if (showFeedback && currentLocationButton) {
      currentLocationButton.classList.add("loading");
      currentLocationButton.disabled = true;
      currentLocationButton.innerHTML = `
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
          <circle cx="12" cy="10" r="3"></circle>
        </svg>
        Getting Location...
      `;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        try {
          const userLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          if (showFeedback) {
            const searchInput = this.querySelector("#provider-search");
            if (searchInput) {
              searchInput.value = "";
            }
            this.searchQuery = "";
          }

          this.setUserLocation(userLocation);
          this.applyFilters();
        } catch (error) {
          console.error("Error processing location:", error);
        } finally {
          this.resetCurrentLocationButton(currentLocationButton, showFeedback);
        }
      },
      (error) => {
        console.warn("Error getting user location:", error);

        if (showFeedback) {
          let errorMessage = "Unable to get your location.";
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage =
                "Location access denied. Please enable location services.";
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = "Location information unavailable.";
              break;
            case error.TIMEOUT:
              errorMessage = "Location request timed out.";
              break;
          }
          alert(errorMessage);
        }

        this.resetCurrentLocationButton(currentLocationButton, showFeedback);
      },
      {
        enableHighAccuracy: false,
        timeout: 15000,
        maximumAge: 300000,
      },
    );
  }

  resetCurrentLocationButton(currentLocationButton, showFeedback) {
    if (!showFeedback || !currentLocationButton) return;

    const defaultLabel =
      currentLocationButton.dataset.defaultLabel || "Use Current Location";

    currentLocationButton.classList.remove("loading");
    currentLocationButton.disabled = false;
    currentLocationButton.innerHTML = `
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
        <circle cx="12" cy="10" r="3"></circle>
      </svg>
      ${defaultLabel}
    `;
  }

  autoLoadCurrentLocation() {
    if (!navigator.geolocation) {
      this.applyFilters();
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        this.applyFilters();
      },
      (error) => {
        console.info("Could not get user location on page load:", error.message);
        this.applyFilters();
      },
      {
        enableHighAccuracy: false,
        timeout: 15000,
        maximumAge: 600000,
      },
    );
  }

  setUserLocation(userLocation) {
    this.userLocation = userLocation;

    if (this.map) {
      this.addUserLocationMarker(userLocation);
    }
  }

  addUserLocationMarker(userLocation) {
    if (!this.map || !window.google || !window.google.maps) return;

    if (this.userLocationMarker) {
      this.userLocationMarker.setMap(null);
    }

    this.userLocationMarker = new google.maps.Marker({
      position: userLocation,
      map: this.map,
      title: "Your Current Location",
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 12,
        fillColor: "#e7a62d",
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 3,
      },
      zIndex: 1000,
    });

    const userInfoWindow = new google.maps.InfoWindow({
      content: `
        <div style="padding: 15px; max-width: 280px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
          <h4 style="margin: 0 0 12px 0; color: #2c3e50; font-size: 16px; font-weight: 700;">Your Current Location</h4>
          <p style="margin: 0; color: #7f8c8d; font-size: 14px; line-height: 1.4;">This is your current location based on your device's GPS.</p>
        </div>
      `,
    });

    this.userLocationMarker.addListener("click", () => {
      this.closeAllInfoWindows();
      userInfoWindow.open(this.map, this.userLocationMarker);
      this.activeInfoWindow = userInfoWindow;
    });
  }

  addSearchLocationMarker(searchLocation) {
    if (!this.map || !window.google || !window.google.maps || !searchLocation) return;

    if (this.searchLocationMarker) {
      this.searchLocationMarker.setMap(null);
    }

    this.searchLocationMarker = new google.maps.Marker({
      position: searchLocation,
      map: this.map,
      title: `Search ZIP ${searchLocation.label || ""}`.trim(),
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: "#b12f2f",
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 3,
      },
      zIndex: 1000,
    });

    const searchInfoWindow = new google.maps.InfoWindow({
      content: `
        <div style="padding: 15px; max-width: 280px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
          <h4 style="margin: 0 0 12px 0; color: #2c3e50; font-size: 16px; font-weight: 700;">ZIP Search Origin</h4>
          <p style="margin: 0; color: #7f8c8d; font-size: 14px; line-height: 1.4;">Showing providers nearest to ZIP ${searchLocation.label || ""}.</p>
        </div>
      `,
    });

    this.searchLocationMarker.addListener("click", () => {
      this.closeAllInfoWindows();
      searchInfoWindow.open(this.map, this.searchLocationMarker);
      this.activeInfoWindow = searchInfoWindow;
    });
  }

  updateOriginMarkers() {
    const showingSearchLocation = Boolean(this.searchFallback && this.searchLocation);

    if (showingSearchLocation) {
      this.addSearchLocationMarker(this.searchLocation);
    } else if (this.searchLocationMarker) {
      this.searchLocationMarker.setMap(null);
    }

    if (!showingSearchLocation && this.userLocation) {
      this.addUserLocationMarker(this.userLocation);
    } else if (this.userLocationMarker) {
      this.userLocationMarker.setMap(null);
    }
  }

  isMeaningfulProviderValue(value) {
    if (value === undefined || value === null) return false;

    const normalizedValue = String(value).trim().toLowerCase();
    return (
      normalizedValue !== "" &&
      normalizedValue !== "false" &&
      normalizedValue !== "null" &&
      normalizedValue !== "undefined"
    );
  }

  getProviderDisplayAddress(provider) {
    if (!this.isMeaningfulProviderValue(provider?.address)) return "";
    return String(provider.address).trim();
  }

  decorateProvider(provider) {
    const displayAddress = this.getProviderDisplayAddress(provider);
    const addressParts = this.parseAddressComponents(displayAddress);
    const coordinates = displayAddress ? this.getProviderCoordinates(provider) : null;

    return {
      ...provider,
      _displayAddress: displayAddress,
      _addressParts: addressParts,
      _coordinates: coordinates,
      _distanceMiles: null,
      _displayDistanceMiles: null,
      _searchIndex: this.buildSearchIndex(provider, addressParts, displayAddress),
    };
  }

  parseAddressComponents(address) {
    const addressParts = {
      street: "",
      city: "",
      state: "",
      zip: "",
      country: "",
    };

    if (!address) return addressParts;

    const remaining = address
      .split(",")
      .map((segment) => segment.trim())
      .filter(Boolean);

    if (remaining.length === 0) return addressParts;

    // Parse from the END so we tolerate any segment count: 3-segment
    // ("Street, City, ST ZIP"), 4-segment (with suite), or the full
    // 5-7-segment Google-style format with explicit country.
    const ZIP_ONLY = /^\d{5}(?:-\d{4})?$/;
    const STATE_AND_ZIP = /^(.+?)\s+(\d{5}(?:-\d{4})?)$/;
    const COUNTRY = /^(united states|usa|u\.s\.a\.|us)$/i;

    const tail = remaining[remaining.length - 1];
    if (ZIP_ONLY.test(tail)) {
      addressParts.zip = remaining.pop();
    } else {
      const stateZipMatch = STATE_AND_ZIP.exec(tail);
      if (stateZipMatch) {
        addressParts.state = stateZipMatch[1].trim();
        addressParts.zip = stateZipMatch[2];
        remaining.pop();
      }
    }

    if (remaining.length > 0 && COUNTRY.test(remaining[remaining.length - 1])) {
      addressParts.country = remaining.pop();
    }

    if (!addressParts.state && remaining.length > 0) {
      addressParts.state = remaining.pop();
    }

    if (remaining.length === 1) {
      addressParts.city = remaining[0];
    } else if (remaining.length > 1) {
      addressParts.city = remaining[remaining.length - 1];
      addressParts.street = remaining.slice(0, -1).join(", ");
    }

    return addressParts;
  }

  buildSearchIndex(provider, addressParts, displayAddress = "") {
    const state = this.normalizeText(addressParts.state);
    const stateAbbreviation = STATE_ABBREVIATIONS[state] || "";
    const searchableFields = [
      provider.location_name,
      displayAddress,
      addressParts.street,
      addressParts.city,
      addressParts.state,
      stateAbbreviation,
      addressParts.zip,
    ];

    return searchableFields
      .map((field) => this.normalizeText(field))
      .filter(Boolean)
      .join(" ");
  }

  normalizeText(value) {
    return (value || "")
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  getSearchQueryTokens(query = this.searchQuery) {
    const normalizedQuery = this.normalizeText(query);
    if (!normalizedQuery) return [];
    return normalizedQuery.split(" ").filter(Boolean);
  }

  matchesSearchQuery(provider, queryTokens = []) {
    if (queryTokens.length === 0) return true;

    return queryTokens.every((token) => provider._searchIndex.includes(token));
  }

  getProviderCoordinates(provider) {
    const latitude = parseFloat(
      provider.geo_coordinates?.latitude || provider.geo_coordinates?.x,
    );
    const longitude = parseFloat(
      provider.geo_coordinates?.longitude || provider.geo_coordinates?.y,
    );

    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      return null;
    }

    return { lat: latitude, lng: longitude };
  }

  getGeocoder() {
    if (!window.google || !window.google.maps) return null;

    if (!this.geocoder) {
      this.geocoder = new google.maps.Geocoder();
    }

    return this.geocoder;
  }

  geocodeSearchQuery(query) {
    const geocoder = this.getGeocoder();

    if (!geocoder || !query) {
      return Promise.resolve(null);
    }

    return new Promise((resolve) => {
      geocoder.geocode(
        {
          address: query,
          componentRestrictions: {
            country: "US",
          },
        },
        (results, status) => {
          if (
            status !== "OK" ||
            !Array.isArray(results) ||
            results.length === 0 ||
            !results[0]?.geometry?.location
          ) {
            resolve(null);
            return;
          }

          const location = results[0].geometry.location;
          const types = Array.isArray(results[0].types) ? results[0].types : [];

          resolve({
            lat: location.lat(),
            lng: location.lng(),
            label: query.trim(),
            types,
          });
        },
      );
    });
  }

  calculateDistanceMiles(from, to) {
    if (!from || !to) return null;

    const earthRadiusMiles = 3958.8;
    const latitudeDelta = this.toRadians(to.lat - from.lat);
    const longitudeDelta = this.toRadians(to.lng - from.lng);
    const fromLatitude = this.toRadians(from.lat);
    const toLatitude = this.toRadians(to.lat);

    const haversine =
      Math.sin(latitudeDelta / 2) * Math.sin(latitudeDelta / 2) +
      Math.cos(fromLatitude) *
        Math.cos(toLatitude) *
        Math.sin(longitudeDelta / 2) *
        Math.sin(longitudeDelta / 2);

    const angularDistance =
      2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));

    return earthRadiusMiles * angularDistance;
  }

  toRadians(value) {
    return (value * Math.PI) / 180;
  }

  formatDistanceMiles(distanceMiles) {
    if (!Number.isFinite(distanceMiles)) return "";

    if (distanceMiles < 10) {
      return `${distanceMiles.toFixed(1)} mi`;
    }

    return `${Math.round(distanceMiles)} mi`;
  }

  getDistanceLabel(provider) {
    const formattedDistance = this.formatDistanceMiles(
      provider._displayDistanceMiles,
    );
    if (!formattedDistance) return "";

    if (this.searchFallback && this.searchLocation) {
      if (this.userLocation) {
        return `${formattedDistance} from your location`;
      }

      return `${formattedDistance} from ${this.searchFallback.query}`;
    }

    return `${formattedDistance} away`;
  }

  getAdaptiveNearbyProviders(providers) {
    const distanceSortedProviders = providers.filter((provider) =>
      Number.isFinite(provider._distanceMiles),
    );

    if (distanceSortedProviders.length === 0) {
      return { providers, radiusMiles: null };
    }

    for (const radiusMiles of this.nearbyRadiusSteps) {
      const visibleProviders =
        radiusMiles === Infinity
          ? distanceSortedProviders.slice(
              0,
              Math.min(this.minimumNearbyResults, distanceSortedProviders.length),
            )
          : distanceSortedProviders.filter(
              (provider) => provider._distanceMiles <= radiusMiles,
            );

      if (
        visibleProviders.length >= this.minimumNearbyResults ||
        radiusMiles === Infinity
      ) {
        return { providers: visibleProviders, radiusMiles };
      }
    }

    return { providers: distanceSortedProviders, radiusMiles: Infinity };
  }

  getCappedExpandedProviders(providers, maxResults = this.maximumExpandedResults) {
    const distanceSorted = providers.filter((provider) =>
      Number.isFinite(provider._distanceMiles),
    );

    if (distanceSorted.length === 0) {
      return { providers: [], radiusMiles: null };
    }

    for (const radiusMiles of this.nearbyRadiusSteps) {
      const within =
        radiusMiles === Infinity
          ? distanceSorted
          : distanceSorted.filter(
              (provider) => provider._distanceMiles <= radiusMiles,
            );

      if (within.length >= maxResults || radiusMiles === Infinity) {
        return {
          providers: within.slice(0, maxResults),
          radiusMiles,
        };
      }
    }

    return {
      providers: distanceSorted.slice(0, maxResults),
      radiusMiles: Infinity,
    };
  }

  isGeocodeUsableForExpansion(geocodeResult) {
    if (!geocodeResult) return false;
    const types = geocodeResult.types || [];
    if (types.includes("country")) return false;
    if (
      types.includes("administrative_area_level_1") &&
      !types.includes("locality") &&
      !types.includes("postal_code") &&
      !types.includes("street_address")
    ) {
      return false;
    }
    return true;
  }

  prioritizeSelectedProvider(providers) {
    const selectedLocationId = this.getSelectedLocationId();
    if (!selectedLocationId) return providers;

    return [...providers].sort((leftProvider, rightProvider) => {
      if (leftProvider.id === selectedLocationId) return -1;
      if (rightProvider.id === selectedLocationId) return 1;
      return 0;
    });
  }

  getRenderedProviders(providers = this.filteredProviders) {
    const selectedLocationId = this.getSelectedLocationId();
    if (!selectedLocationId) return providers;

    const prioritizedProviders = this.prioritizeSelectedProvider(providers);
    const selectedProviderIsPresent = prioritizedProviders.some(
      (provider) => provider.id === selectedLocationId,
    );

    if (selectedProviderIsPresent) {
      return prioritizedProviders;
    }

    const selectedProvider = this.providers.find(
      (provider) => provider.id === selectedLocationId,
    );

    if (!selectedProvider) {
      return prioritizedProviders;
    }

    const displayOrigin = this.userLocation || this.activeDistanceOrigin;
    const [decoratedSelectedProvider] = this.getProvidersWithDistance(
      [selectedProvider],
      this.activeDistanceOrigin,
      displayOrigin,
    );

    return [decoratedSelectedProvider, ...prioritizedProviders];
  }

  isSelectedProviderPinned(renderedProviders = this.getRenderedProviders()) {
    return renderedProviders.length > this.filteredProviders.length;
  }

  getProvidersWithDistance(providers, origin = null, displayOrigin = origin) {
    return providers.map((provider) => ({
      ...provider,
      _distanceMiles: origin
        ? this.calculateDistanceMiles(origin, provider._coordinates)
        : null,
      _displayDistanceMiles: displayOrigin
        ? this.calculateDistanceMiles(displayOrigin, provider._coordinates)
        : null,
    }));
  }

  sortProvidersByDistance(providers) {
    return [...providers].sort((leftProvider, rightProvider) => {
      const leftDistance = Number.isFinite(leftProvider._distanceMiles)
        ? leftProvider._distanceMiles
        : Number.POSITIVE_INFINITY;
      const rightDistance = Number.isFinite(rightProvider._distanceMiles)
        ? rightProvider._distanceMiles
        : Number.POSITIVE_INFINITY;

      if (leftDistance !== rightDistance) {
        return leftDistance - rightDistance;
      }

      return leftProvider.location_name.localeCompare(rightProvider.location_name);
    });
  }

  async applyFilters(options = {}) {
    if (!this.providersLoaded) return;
    const { deferMapUpdate = false } = options;

    const requestToken = ++this.searchRequestToken;
    const trimmedQuery = this.searchQuery.trim();
    const queryTokens = this.getSearchQueryTokens(trimmedQuery);

    this.searchLocation = null;
    this.searchFallback = null;
    this.activeDistanceOrigin = this.userLocation;

    const exactMatchProviders = this.providers.filter((provider) =>
      this.matchesSearchQuery(provider, queryTokens),
    );

    let filteredProviders = this.getProvidersWithDistance(
      exactMatchProviders,
      this.activeDistanceOrigin,
      this.userLocation || this.activeDistanceOrigin,
    );

    if (trimmedQuery) {
      const searchLocation = await this.geocodeSearchQuery(trimmedQuery);

      if (requestToken !== this.searchRequestToken) {
        return;
      }

      if (searchLocation && this.isGeocodeUsableForExpansion(searchLocation)) {
        this.searchLocation = searchLocation;
        this.activeDistanceOrigin = searchLocation;
        const displayOrigin = this.userLocation || searchLocation;

        const exactWithDistance = this.sortProvidersByDistance(
          this.getProvidersWithDistance(
            exactMatchProviders,
            searchLocation,
            displayOrigin,
          ),
        );

        const exactIds = new Set(exactMatchProviders.map((provider) => provider.id));
        const candidatePool = this.providers.filter(
          (provider) => !exactIds.has(provider.id),
        );
        const candidatesWithDistance = this.sortProvidersByDistance(
          this.getProvidersWithDistance(candidatePool, searchLocation, displayOrigin),
        );

        const expandedState = this.getCappedExpandedProviders(candidatesWithDistance);
        const expandedProviders = expandedState.providers.map((provider) => ({
          ...provider,
          _isExpandedMatch: true,
        }));

        filteredProviders = [...exactWithDistance, ...expandedProviders];

        this.searchFallback = {
          type: exactWithDistance.length === 0 ? "expanded-only" : "exact-and-expanded",
          query: trimmedQuery,
          exactCount: exactWithDistance.length,
          expandedCount: expandedProviders.length,
        };
        this.activeRadiusMiles = expandedState.radiusMiles;
      } else if (this.activeDistanceOrigin) {
        filteredProviders = this.sortProvidersByDistance(filteredProviders);
        this.activeRadiusMiles = null;
      } else {
        filteredProviders = this.prioritizeSelectedProvider(filteredProviders);
        this.activeRadiusMiles = null;
      }
    } else if (this.activeDistanceOrigin) {
      filteredProviders = this.sortProvidersByDistance(filteredProviders);
    } else {
      filteredProviders = this.prioritizeSelectedProvider(filteredProviders);
    }

    if (!this.searchFallback) {
      this.activeDistanceOrigin = this.userLocation;
    }

    if (this.userLocation && !trimmedQuery) {
      filteredProviders.sort((leftProvider, rightProvider) => {
        const leftDistance = Number.isFinite(leftProvider._distanceMiles)
          ? leftProvider._distanceMiles
          : Number.POSITIVE_INFINITY;
        const rightDistance = Number.isFinite(rightProvider._distanceMiles)
          ? rightProvider._distanceMiles
          : Number.POSITIVE_INFINITY;

        if (leftDistance !== rightDistance) {
          return leftDistance - rightDistance;
        }

        return leftProvider.location_name.localeCompare(rightProvider.location_name);
      });

      this.activeDistanceOrigin = this.userLocation;
      const nearbyProvidersState = this.getAdaptiveNearbyProviders(filteredProviders);
      filteredProviders = nearbyProvidersState.providers;
      this.activeRadiusMiles = nearbyProvidersState.radiusMiles;
    } else if (!this.searchFallback) {
      this.activeRadiusMiles = null;
    }

    if (requestToken !== this.searchRequestToken) {
      return;
    }

    this.filteredProviders = filteredProviders;
    this.renderResults();

    if (deferMapUpdate) {
      if (this.isMobileListViewActive()) {
        this.clearPendingMapUpdate();
      } else {
        this.scheduleMapUpdate();
      }
    } else {
      this.clearPendingMapUpdate();
      if (!this.isMobileListViewActive()) {
        this.updateMapMarkers();
      }
    }
  }

  renderResults() {
    const resultsList = this.querySelector("#provider-list");
    const resultsCount = this.querySelector(".results-count");

    if (!resultsList || !resultsCount) return;
    this.renderRequestToken += 1;
    const renderRequestToken = this.renderRequestToken;
    this.clearPendingRender();
    const renderedProviders = this.getRenderedProviders();

    const resultText = resultsCount.dataset.resultText || "result";
    const filteredCount = this.filteredProviders.length;
    const filteredLabel = `${filteredCount} ${resultText}${filteredCount !== 1 ? "s" : ""}`;
    resultsCount.textContent = this.isSelectedProviderPinned(renderedProviders)
      ? `${filteredLabel} + selected provider`
      : filteredLabel;

    this.updateDataSourceIndicator();
    this.updateResultsSummary();

    if (renderedProviders.length === 0) {
      const noResultsTemplate = document.getElementById("no-results-template");
      const noResultsElement = noResultsTemplate.content.cloneNode(true);
      resultsList.replaceChildren(noResultsElement);
      return;
    }

    const shouldChunkRender =
      this.mobileMediaQuery?.matches &&
      renderedProviders.length > this.mobileRenderChunkThreshold;

    if (!shouldChunkRender) {
      const fragment = document.createDocumentFragment();
      renderedProviders.forEach((provider) => {
        fragment.appendChild(this.createProviderCard(provider));
      });
      resultsList.replaceChildren(fragment);
      this.prePopulateSelectedProvider();
      return;
    }

    resultsList.replaceChildren();

    let providerIndex = 0;
    const renderBatch = () => {
      if (renderRequestToken !== this.renderRequestToken) return;

      const fragment = document.createDocumentFragment();
      const batchEndIndex = Math.min(
        providerIndex + this.mobileRenderChunkSize,
        renderedProviders.length,
      );

      while (providerIndex < batchEndIndex) {
        fragment.appendChild(this.createProviderCard(renderedProviders[providerIndex]));
        providerIndex += 1;
      }

      resultsList.appendChild(fragment);

      if (providerIndex < renderedProviders.length) {
        this.pendingRenderFrame = window.requestAnimationFrame(renderBatch);
        return;
      }

      this.pendingRenderFrame = null;
      this.prePopulateSelectedProvider();
    };

    renderBatch();
  }

  updateResultsSummary() {
    const resultsSummary = this.querySelector("[data-results-summary]");
    if (!resultsSummary) return;

    if (this.searchFallback?.type === "exact-and-expanded") {
      const { exactCount, expandedCount, query } = this.searchFallback;
      const exactLabel = `${exactCount} exact ${exactCount === 1 ? "match" : "matches"}`;
      const expandedLabel = `${expandedCount} nearby ${expandedCount === 1 ? "provider" : "providers"}`;
      resultsSummary.textContent = expandedCount > 0
        ? `Showing ${exactLabel} for "${query}" plus ${expandedLabel}.`
        : `Showing ${exactLabel} for "${query}".`;
      return;
    }

    if (this.searchFallback?.type === "expanded-only") {
      const { expandedCount, query } = this.searchFallback;
      if (expandedCount === 0) {
        resultsSummary.textContent = `No matches for "${query}".`;
        return;
      }
      const expandedLabel = `${expandedCount} nearby ${expandedCount === 1 ? "provider" : "providers"}`;
      resultsSummary.textContent = `No exact match for "${query}". Showing ${expandedLabel}.`;
      return;
    }

    if (this.userLocation && this.searchQuery) {
      resultsSummary.textContent =
        "Matching providers are sorted by distance from your current location.";
      return;
    }

    if (this.userLocation && this.activeRadiusMiles) {
      if (this.activeRadiusMiles === Infinity) {
        resultsSummary.textContent =
          "Showing the nearest providers nationwide based on your current location.";
        return;
      }

      resultsSummary.textContent = `Showing the nearest providers within ${this.activeRadiusMiles} miles of your current location.`;
      return;
    }

    if (this.searchQuery) {
      resultsSummary.textContent =
        "Search matches street, city, ZIP, state, and location name only.";
      return;
    }

    resultsSummary.textContent = "";
  }

  prePopulateSelectedProvider() {
    const selectedLocationId = this.getSelectedLocationId();
    if (!selectedLocationId) return;

    if (this.querySelector(`[data-provider-id="${selectedLocationId}"]`)) {
      this.updateSelectedCardState(selectedLocationId);
    }
  }

  getSelectedLocationId() {
    if (typeof window.DTCCookieManager !== "undefined") {
      return window.DTCCookieManager.getCookie("location_id");
    }

    return this.getCookie("location_id");
  }

  getCookie(name) {
    const nameEQ = `${name}=`;
    const cookies = document.cookie.split(";");

    for (let index = 0; index < cookies.length; index += 1) {
      let cookie = cookies[index];
      while (cookie.charAt(0) === " ") {
        cookie = cookie.substring(1, cookie.length);
      }
      if (cookie.indexOf(nameEQ) === 0) {
        return cookie.substring(nameEQ.length, cookie.length);
      }
    }

    return null;
  }

  updateDataSourceIndicator() {
    const resultsHeader = this.querySelector(".results-header h3");
    if (resultsHeader) {
      resultsHeader.textContent = "Providers";
    }
  }

  formatPhoneUS(phone) {
    const digits = phone.replace(/\D/g, "");
    if (digits.length === 11 && digits[0] === "1") {
      return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return phone;
  }

  getProviderPhone(provider) {
    if (this.isMeaningfulProviderValue(provider.phone_number)) {
      return String(provider.phone_number).trim();
    }

    if (this.isMeaningfulProviderValue(provider.phone)) {
      return String(provider.phone).trim();
    }

    return "";
  }

  getProviderWebsite(provider) {
    if (!this.isMeaningfulProviderValue(provider.location_website)) return "";
    return String(provider.location_website).trim();
  }

  createProviderCard(provider) {
    const template = document.getElementById("provider-card-template");
    const card = template.content.cloneNode(true);
    const cardElement = card.querySelector(".provider-card");
    const phone = this.getProviderPhone(provider);
    const website = this.getProviderWebsite(provider);
    const distanceLabel = this.getDistanceLabel(provider);

    cardElement.setAttribute("data-provider-id", provider.id);
    card.querySelector(".provider-name").textContent = provider.location_name;
    const addressElement = card.querySelector(".provider-address");
    if (provider._displayAddress) {
      addressElement.textContent = provider._displayAddress;
    } else {
      addressElement.remove();
    }

    if (distanceLabel) {
      const distanceElement = card.querySelector(".provider-distance");
      distanceElement.innerHTML = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>${distanceLabel}`;
      distanceElement.style.display = "inline-flex";
    }

    if (phone) {
      const phoneElement = card.querySelector(".provider-phone");
      const phoneText = phoneElement.querySelector(".provider-phone-number");
      phoneText.textContent = this.formatPhoneUS(phone);
      phoneElement.style.display = "flex";
    }

    if (website) {
      const websiteElement = card.querySelector(".provider-website");
      const websiteText = websiteElement.querySelector(".provider-website-link");
      const link = document.createElement("a");

      link.href = website;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.textContent = website.replace(/^https?:\/\//, "");

      websiteText.innerHTML = "";
      websiteText.appendChild(link);
      websiteElement.style.display = "flex";
    }

    cardElement.addEventListener("click", () => {
      this.selectProvider(provider.id);
    });

    const selectButton = card.querySelector(".select-provider-button");
    if (selectButton) {
      selectButton.addEventListener("click", (event) => {
        event.stopPropagation();
        this.selectProvider(provider.id);
      });
    }

    return card;
  }

  createInfoWindowContent(provider) {
    const template = document.getElementById("map-info-template");
    const infoContent = template.content.cloneNode(true);
    const phone = this.getProviderPhone(provider);
    const website = this.getProviderWebsite(provider);
    const distanceLabel = this.getDistanceLabel(provider);

    infoContent.querySelector(".info-title").textContent = provider.location_name;
    const addressElement = infoContent.querySelector(".info-address");
    if (provider._displayAddress) {
      addressElement.textContent = provider._displayAddress;
    } else {
      addressElement.remove();
    }

    if (distanceLabel) {
      const distanceElement = infoContent.querySelector(".info-distance");
      distanceElement.textContent = distanceLabel;
      distanceElement.style.display = "block";
    }

    if (phone) {
      const phoneElement = infoContent.querySelector(".info-phone");
      const phoneText = phoneElement.querySelector(".info-phone-number");
      phoneText.textContent = this.formatPhoneUS(phone);
      phoneElement.style.display = "flex";
    }

    if (website) {
      const websiteElement = infoContent.querySelector(".info-website");
      const websiteLink = websiteElement.querySelector(".info-website-link");

      websiteLink.href = website;
      websiteLink.textContent = website.replace(/^https?:\/\//, "");
      websiteElement.style.display = "flex";
    }

    const tempDiv = document.createElement("div");
    tempDiv.appendChild(infoContent);
    return tempDiv.innerHTML;
  }

  updateSelectedCardState(providerId) {
    this.querySelectorAll(".provider-card").forEach((card) => {
      card.classList.remove("active");

      const button = card.querySelector(".select-provider-button");
      if (!button) return;

      button.textContent = this.dataset.selectProviderText || "Select Provider";
      button.classList.remove("selected");
    });

    const selectedCard = this.querySelector(`[data-provider-id="${providerId}"]`);
    if (!selectedCard) return;

    selectedCard.classList.add("active");

    const selectedButton = selectedCard.querySelector(".select-provider-button");
    if (selectedButton) {
      selectedButton.textContent =
        this.dataset.providerSelectedText || "SELECTED PROVIDER";
      selectedButton.classList.add("selected");
    }
  }

  selectProvider(providerId) {
    this.updateSelectedCardState(providerId);
    this.scrollProviderCardIntoView(providerId);

    const provider = this.providers.find((providerItem) => providerItem.id === providerId);
    if (provider && provider._coordinates && this.map) {
      this.map.setCenter(provider._coordinates);
      this.map.setZoom(15);
    }

    if (provider) {
      const {
        _addressParts,
        _coordinates,
        _displayAddress,
        _distanceMiles,
        _displayDistanceMiles,
        _isExpandedMatch,
        _searchIndex,
        ...providerData
      } = provider;
      if (typeof window.DTCCookieManager !== "undefined") {
        window.DTCCookieManager.assignProviderSelection(providerData, {
          priorityOverride: "1",
          dtc: true,
          source: "manual-map",
        });
      } else {
        localStorage.setItem("provider_data", JSON.stringify(providerData));
        window.dispatchEvent(
          new CustomEvent("providerSelected", {
            detail: {
              provider: providerData,
              providerId,
              source: "manual-map",
            },
          }),
        );
        console.error(
          "DTCCookieManager not available - please ensure dtc-cookie-manager.js is loaded",
        );
      }
    }

    if (window.location.pathname !== "/cart" || this.initialMapSelect === true) {
      this.showSelectionPopup();
    } else {
      this.initialMapSelect = true;
    }
  }

  scrollProviderCardIntoView(providerId) {
    const selectedCard = this.querySelector(`[data-provider-id="${providerId}"]`);
    if (!selectedCard) return;

    selectedCard.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }

  showSelectionPopup() {
    const selectedLocationId = this.getSelectedLocationId();
    const selectedProvider = this.providers.find(
      (provider) => provider.id === selectedLocationId,
    );
    const providerName = selectedProvider
      ? selectedProvider.location_name
      : "a provider";

    const config = {
      enabled: this.dataset.popupEnabled === "true",
      title: this.dataset.popupTitle || "Provider Selected!",
      message: (
        this.dataset.popupMessage ||
        "Great choice! You've selected [PROVIDER_NAME] as your local provider. What would you like to do next?"
      ).replace("[PROVIDER_NAME]", providerName),
      continueText: this.dataset.popupContinueText || "Continue Shopping",
      cartText: this.dataset.popupCartText || "Go to Cart",
      continueUrl: this.dataset.popupContinueUrl || "/",
      cartUrl: this.dataset.popupCartUrl || "/cart",
      backgroundColor: this.dataset.popupBackgroundColor || "#ffffff",
      textColor: this.dataset.popupTextColor || "#000000",
    };

    if (!config.enabled) return;

    let popup = this.querySelector(".selection-popup");
    if (!popup) {
      const template = document.getElementById("selection-popup-template");
      if (!template) return;

      const popupElement = template.content.cloneNode(true);
      this.appendChild(popupElement);
      popup = this.querySelector(".selection-popup");
    }

    const titleElement = popup.querySelector(".popup-title");
    const messageElement = popup.querySelector(".popup-message");
    const continueButton = popup.querySelector(".popup-continue");
    const cartButton = popup.querySelector(".popup-cart");
    const popupCloseButton = popup.querySelector(".popup-close-button");

    if (titleElement) titleElement.textContent = config.title;
    if (messageElement) messageElement.textContent = config.message;

    if (continueButton) {
      continueButton.textContent = config.continueText;
      continueButton.href = config.continueUrl;
    }

    if (cartButton) {
      cartButton.textContent = config.cartText;
      cartButton.href = config.cartUrl;
    }

    if (popupCloseButton) {
      popupCloseButton.addEventListener("click", () => {
        this.hideSelectionPopup();
      });
    }

    const popupContent = popup.querySelector(".popup-content");
    if (popupContent) {
      popupContent.style.backgroundColor = config.backgroundColor;
      popupContent.style.color = config.textColor;
    }

    popup.style.display = "flex";
    requestAnimationFrame(() => {
      popup.classList.add("visible");
    });

    const closeButton = popup.querySelector(".popup-close");
    if (closeButton) {
      closeButton.onclick = () => this.hideSelectionPopup();
    }

    popup.onclick = (event) => {
      if (event.target === popup) {
        this.hideSelectionPopup();
      }
    };

    if (popupContent) {
      popupContent.onclick = (event) => event.stopPropagation();
    }

    setTimeout(() => {
      if (popup && popup.classList.contains("visible")) {
        this.hideSelectionPopup();
      }
    }, 15000);
  }

  hideSelectionPopup() {
    const popup = this.querySelector(".selection-popup");
    if (!popup) return;

    popup.classList.remove("visible");
    setTimeout(() => {
      popup.style.display = "none";
    }, 300);
  }

  showMapError(message) {
    const mapPlaceholder = this.querySelector(".map-placeholder");
    if (mapPlaceholder) {
      mapPlaceholder.innerHTML = `<p>${message}</p>`;
    }
  }

  loadGoogleMaps() {
    const apiKey = this.dataset.googleMapsApiKey;

    if (!apiKey) {
      this.showMapError("Please configure Google Maps API key in theme settings.");
      return;
    }

    if (window.google && window.google.maps) {
      this.initMap();
      return;
    }

    if (this._mapsScriptAppended) return;
    this._mapsScriptAppended = true;

    const previousAuthFailure = window.gm_authFailure;
    window.gm_authFailure = () => {
      if (typeof previousAuthFailure === "function") {
        previousAuthFailure();
      }

      this.showMapError(
        "Google Maps could not load. Check the API key, billing, Maps JavaScript API, and allowed domains.",
      );
    };

    // The callback name is interpolated into a JS identifier, so the element id
    // must be sanitized — ids like "provider-locator" would otherwise produce
    // `initProvidersMap_provider-locator`, which Google evaluates as a
    // subtraction expression and throws a ReferenceError.
    const callbackName = `initProvidersMap_${(this.id || "default").replace(
      /[^a-zA-Z0-9_$]/g,
      "_",
    )}`;

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async&callback=${callbackName}`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      this.showMapError("Google Maps could not load. Check the API key and network access.");
    };

    window[callbackName] = () => {
      this.initMap();
    };

    document.head.appendChild(script);
  }

  initMap() {
    const mapContainer = this.querySelector("#google-map");
    if (!mapContainer || !window.google || !window.google.maps) return;

    let defaultCenter = { lat: 39.5, lng: -98.35 };
    let defaultZoom = 4;

    if (this.userLocation) {
      defaultCenter = this.userLocation;
      defaultZoom = 11;
    }

    this.map = new google.maps.Map(mapContainer, {
      zoom: defaultZoom,
      center: defaultCenter,
      styles: [
        {
          featureType: "all",
          elementType: "geometry.fill",
          stylers: [{ color: "#f8f9fa" }],
        },
        {
          featureType: "all",
          elementType: "geometry.stroke",
          stylers: [{ color: "#e9ecef" }],
        },
        {
          featureType: "water",
          elementType: "geometry",
          stylers: [{ color: "#4ecdc4" }, { lightness: 20 }],
        },
        {
          featureType: "road",
          elementType: "geometry",
          stylers: [{ color: "#ffffff" }],
        },
        {
          featureType: "road.highway",
          elementType: "geometry",
          stylers: [{ color: "#ff6b6b" }, { lightness: 40 }],
        },
        {
          featureType: "poi",
          elementType: "labels",
          stylers: [{ visibility: "off" }],
        },
        {
          featureType: "transit",
          elementType: "labels",
          stylers: [{ visibility: "off" }],
        },
        {
          featureType: "landscape",
          elementType: "geometry",
          stylers: [{ color: "#f1f3f4" }],
        },
      ],
      disableDefaultUI: false,
      zoomControl: true,
      mapTypeControl: false,
      scaleControl: true,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: true,
    });

    if (this.userLocation) {
      this.addUserLocationMarker(this.userLocation);
    }

    this.applyFilters();
  }

  updateMapMarkers() {
    if (!this.map) return;

    this.updateOriginMarkers();
    this.addMarkers();
    this.updateMapViewport();
  }

  addMarkers() {
    this.clearMarkers();

    this.filteredProviders.forEach((provider) => {
      if (!provider._coordinates) {
        return;
      }

      const marker = new google.maps.Marker({
        position: provider._coordinates,
        map: this.map,
        title: provider.location_name,
      });

      const infoWindow = new google.maps.InfoWindow({
        content: this.createInfoWindowContent(provider),
      });

      marker.addListener("click", () => {
        this.closeAllInfoWindows();
        infoWindow.open(this.map, marker);
        this.activeInfoWindow = infoWindow;
        this.selectProvider(provider.id);
      });

      this.markers.push({ marker, infoWindow });
    });
  }

  updateMapViewport() {
    if (!this.map || !window.google || !window.google.maps) return;

    const bounds = new google.maps.LatLngBounds();
    let boundsHavePoints = false;
    const viewportOrigin =
      this.searchFallback && this.searchLocation
        ? this.searchLocation
        : this.userLocation;

    if (viewportOrigin) {
      bounds.extend(viewportOrigin);
      boundsHavePoints = true;
    }

    this.filteredProviders.forEach((provider) => {
      if (!provider._coordinates) return;
      bounds.extend(provider._coordinates);
      boundsHavePoints = true;
    });

    if (!boundsHavePoints) return;

    if (viewportOrigin && this.filteredProviders.length === 0) {
      this.map.setCenter(viewportOrigin);
      this.map.setZoom(11);
      return;
    }

    if (!viewportOrigin) {
      this.map.setCenter({ lat: 39.5, lng: -98.35 });
      this.map.setZoom(4);
      return;
    }

    this.map.fitBounds(bounds, 80);

    google.maps.event.addListenerOnce(this.map, "bounds_changed", () => {
      const maxZoom = viewportOrigin ? 14 : 15;
      if (this.map.getZoom() > maxZoom) {
        this.map.setZoom(maxZoom);
      }
    });
  }

  closeAllInfoWindows() {
    if (this.activeInfoWindow) {
      this.activeInfoWindow.close();
      this.activeInfoWindow = null;
    }

    this.markers.forEach(({ infoWindow }) => {
      infoWindow.close();
    });
  }

  clearMarkers() {
    this.markers.forEach(({ marker }) => {
      marker.setMap(null);
    });
    this.markers = [];
  }

  disconnectedCallback() {
    this.clearPendingSearch();
    this.clearPendingMapUpdate();
    this.clearPendingRender();
  }
}

if (!customElements.get("providers-map")) {
  customElements.define("providers-map", ProvidersMap);
}
